import { useParams, Link, useNavigate } from 'react-router-dom';
import { blogPosts } from '../../mocks/blogPosts';
import BlogNavbar from './components/BlogNavbar';
import BlogFooter from './components/BlogFooter';
import SEOHead from '../../components/feature/SEOHead';

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const post = blogPosts.find((p) => p.slug === slug);

  const related = blogPosts
    .filter((p) => p.slug !== slug)
    .slice(0, 3);

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-petrol-800 mb-4">Post não encontrado</h1>
          <Link to="/blog" className="text-orange-600 hover:text-orange-700 font-medium">
            Voltar para o blog
          </Link>
        </div>
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.image,
    "datePublished": post.date,
    "author": {
      "@type": "Person",
      "name": post.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "IBX Brasil",
      "logo": {
        "@type": "ImageObject",
        "url": "https://ibxbrasil.com.br/logo.png"
      }
    }
  };

  return (
    <>
      <SEOHead
        title={`${post.title} | Blog IBX Brasil`}
        description={post.excerpt}
        canonical={`https://ibxbrasil.com.br/blog/${post.slug}`}
        ogImage={post.image}
        ogType="article"
        schema={articleSchema}
      />
      <div className="min-h-screen bg-white">
        <BlogNavbar />

        {/* Hero */}
        <section className="pt-28 pb-0 bg-stone-900">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 pb-12">
            <a
              href="/blog"
              className="inline-flex items-center justify-center px-6 py-3 bg-action-orange-500 text-white font-semibold rounded-lg hover:bg-action-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Voltar para o Blog
            </a>
            <span className="inline-block bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full mb-5">
              {post.category}
            </span>
            <h1
              className="text-3xl lg:text-5xl font-bold text-white mb-6 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {post.title}
            </h1>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-orange-400"></i> {post.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-orange-400"></i> {post.readTime} de leitura
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-map-pin-line text-orange-400"></i> Vitória da Conquista – BA
              </span>
            </div>
          </div>
        </section>

        {/* Cover Image */}
        <div className="max-w-4xl mx-auto px-6 lg:px-8 -mt-0">
          <div className="w-full h-72 lg:h-96 rounded-2xl overflow-hidden shadow-xl bg-stone-200">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover object-top"
              loading="lazy"
              decoding="async"
              width="800"
              height="480"
            />
          </div>
        </div>

        {/* Content */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Article */}
              <article className="lg:col-span-2">
                <p className="text-base text-gray-600 leading-relaxed mb-8 font-medium border-l-4 border-orange-500 pl-5 italic">
                  {post.excerpt}
                </p>
                <div
                  className="prose prose-stone max-w-none text-sm leading-relaxed text-gray-700
                    [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:font-serif
                    [&_p]:mb-5 [&_p]:leading-relaxed
                    [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-5 [&_ul]:space-y-2
                    [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-5 [&_ol]:space-y-2
                    [&_li]:text-gray-600
                    [&_strong]:text-gray-900 [&_strong]:font-semibold"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />

                {/* Share */}
                <div className="mt-12 pt-8 border-t border-gray-100">
                  <p className="text-sm font-semibold text-gray-700 mb-4">Compartilhar artigo:</p>
                  <div className="flex gap-3">
                    {[
                      { icon: 'ri-whatsapp-line', label: 'WhatsApp', color: 'bg-green-500 hover:bg-green-600', href: `https://wa.me/?text=${encodeURIComponent(post.title + ' - ' + window.location.href)}` },
                      { icon: 'ri-linkedin-fill', label: 'LinkedIn', color: 'bg-sky-700 hover:bg-sky-800', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}` },
                      { icon: 'ri-facebook-fill', label: 'Facebook', color: 'bg-indigo-600 hover:bg-indigo-700', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}` },
                    ].map((s, i) => (
                      <a
                        key={i}
                        href={s.href}
                        target="_blank"
                        rel="nofollow noreferrer"
                        className={`inline-flex items-center gap-2 ${s.color} text-white px-4 py-2 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap cursor-pointer`}
                      >
                        <i className={`${s.icon} text-sm`}></i>
                        {s.label}
                      </a>
                    ))}
                  </div>
                </div>
              </article>

              {/* Sidebar */}
              <aside className="lg:col-span-1">
                {/* CTA Card */}
                <div className="bg-stone-900 rounded-2xl p-7 mb-8 sticky top-28">
                  <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center mb-4">
                    <i className="ri-shield-check-line text-xl text-orange-400"></i>
                  </div>
                  <h4
                    className="text-base font-bold text-white mb-3 leading-snug"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Solicite uma Análise Gratuita de Riscos
                  </h4>
                  <p className="text-xs text-gray-400 leading-relaxed mb-5">
                    Nossa equipe analisa seu perfil e apresenta as melhores coberturas com condições vantajosas.
                  </p>
                  <a
                    href="https://wa.me/5577988080505?text=Ol%C3%A1%2C%20gostaria%20de%20solicitar%20uma%20an%C3%A1lise%20de%20riscos."
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="flex items-center justify-center gap-2 bg-green-500 text-white px-5 py-3 rounded-lg text-xs font-semibold hover:bg-green-600 transition-colors whitespace-nowrap cursor-pointer mb-3"
                  >
                    <i className="ri-whatsapp-line text-sm"></i>
                    Falar no WhatsApp
                  </a>
                  <a
                    href="/#contato"
                    className="flex items-center justify-center gap-2 bg-orange-500 text-white px-5 py-3 rounded-lg text-xs font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-file-search-line text-sm"></i>
                    Solicitar Análise
                  </a>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Related Posts */}
        <section className="py-16 bg-stone-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h3
              className="text-2xl font-bold text-gray-900 mb-10"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Artigos Relacionados
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              {related.map(r => (
                <article
                  key={r.id}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-100 hover:border-orange-100"
                  onClick={() => navigate(`/blog/${r.slug}`)}
                >
                  <div className="relative overflow-hidden h-48 bg-stone-200">
                    <img
                      src={r.image}
                      alt={r.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                      width="800"
                      height="480"
                    />
                    <span className="absolute top-3 left-3 bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      {r.category}
                    </span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                      <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {r.date}</span>
                      <span className="flex items-center gap-1"><i className="ri-time-line"></i> {r.readTime}</span>
                    </div>
                    <h4
                      className="text-sm font-bold text-gray-900 mb-2 leading-snug group-hover:text-orange-500 transition-colors line-clamp-2"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {r.title}
                    </h4>
                    <span className="inline-flex items-center gap-1 text-orange-500 text-xs font-semibold group-hover:gap-2 transition-all">
                      Ler mais <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <BlogFooter />
      </div>
    </>
  );
}
